import { useAuth0 } from '@auth0/auth0-react';
import { Button, ErrorState } from '@syncanix/ui';
import { useId } from 'react';

import { OutsideShellHeader } from '@/components/OutsideShellHeader';
import { useT } from '@/core/providers/I18nProvider';
import { useTermsAcceptance } from '@/core/providers/terms-acceptance-context';
import { TSlots } from '@/core/providers/TSlots';
import { PRIVACY_URL, TERMS_URL } from '@/lib/legal-links';

import { TermsLanguagePicker } from './TermsLanguagePicker';

import type { ReactElement } from 'react';

export interface TermsAcceptancePageProps {
  /**
   * The version this person accepted previously, if any. Non-null means the terms
   * were REPUBLISHED — the copy changes to say so, because greeting a
   * long-standing customer as though they had never agreed to anything reads as a
   * bug and invites them to distrust the screen.
   */
  readonly previouslyAcceptedVersion: string | null;
}

/**
 * The Terms-of-Service acceptance interstitial (ADR-0497).
 *
 * Rendered by `TermsGate` INSTEAD of the router, so it is a full screen rather
 * than a modal. That is deliberate: a dialog would need a portal, a focus trap, a
 * scroll lock and a viewport clamp (`ui-design.md` § 11), and every one of those is
 * a way to shift the layout of a page that is, in this case, not there at all. A
 * screen has none of that machinery and cannot jump.
 *
 * Design contract:
 *
 *   - ONE primary action, sentence case, action-worded (§ 1).
 *   - An explicit, deliberate commit — never instant-apply (§ 15). It changes a
 *     legal relationship.
 *   - A visible way OUT. Without sign-out the user is trapped on a screen with a
 *     single button, which is coercive rather than a choice: they must be able to
 *     decline and leave.
 *   - Links open in a new tab, so reading the terms never costs the user their
 *     place — a same-tab navigation would unmount this screen and, since the gate
 *     re-renders it on return, silently discard nothing but feel like a reset.
 *   - Copy is one slotted message (`<TSlots>`), never `before` + link + `after`
 *     keys joined with `{' '}`. That pattern bakes English spacing into the markup
 *     and rendered "ל תנאי השירות" in Hebrew, which attaches the ל preposition to
 *     the following word — the exact bug the billing consent line shipped with.
 *   - No physical directional classes; the layout is flex + logical spacing, so it
 *     mirrors correctly for AR/HE.
 *   - A LANGUAGE PICKER, first on the card. Consent has to be informed, and this is
 *     the one screen a user cannot navigate away from — the dashboard's own
 *     switcher is behind this gate. See `TermsLanguagePicker`.
 */
export function TermsAcceptancePage({
  previouslyAcceptedVersion,
}: TermsAcceptancePageProps): ReactElement {
  const { t } = useT();
  const { logout } = useAuth0();
  const { accept, accepting, acceptFailed } = useTermsAcceptance();
  const headingId = useId();
  const errorId = useId();

  const updated = previouslyAcceptedVersion !== null;

  return (
    <div className="flex min-h-screen flex-col bg-background text-foreground">
      <OutsideShellHeader />
      {/* `flex-1` + centring keeps the card optically centred on tall screens and
          lets it scroll naturally on short ones — the content is the page, so there
          is no inner scroller to strand a button below the fold. */}
      <main className="flex flex-1 items-center justify-center p-6">
        <section
          aria-labelledby={headingId}
          className="w-full max-w-md space-y-6 rounded-lg border border-border bg-surface p-6 shadow-sm"
        >
          {/* FIRST, above the heading. A reader who cannot read this screen needs
              the language control before anything else on it, and putting it after
              the copy would mean scanning past text they cannot read to find it. */}
          <TermsLanguagePicker />

          <div className="space-y-2">
            <h1 id={headingId} className="text-xl font-semibold">
              {updated ? t('terms.gate.updatedTitle') : t('terms.gate.title')}
            </h1>
            <p className="text-sm text-muted-foreground">
              {updated ? t('terms.gate.updatedBody') : t('terms.gate.body')}
            </p>
          </div>

          <p className="text-sm">
            <TSlots
              id="terms.gate.consent"
              slots={{
                terms: (
                  <a
                    href={TERMS_URL}
                    target="_blank"
                    rel="noreferrer"
                    className="text-link underline underline-offset-2"
                  >
                    {t('terms.gate.termsLink')}
                  </a>
                ),
                privacy: (
                  <a
                    href={PRIVACY_URL}
                    target="_blank"
                    rel="noreferrer"
                    className="text-link underline underline-offset-2"
                  >
                    {t('terms.gate.privacyLink')}
                  </a>
                ),
              }}
            />
          </p>

          {acceptFailed ? (
            <ErrorState
              id={errorId}
              title={t('terms.gate.errorTitle')}
              description={t('terms.gate.errorBody')}
            />
          ) : null}

          <div className="flex flex-col gap-3">
            <Button
              onClick={() => {
                void accept();
              }}
              disabled={accepting}
              // Announced so a screen-reader user is told the button is working
              // rather than left wondering whether the click registered (§ 3).
              aria-busy={accepting}
              {...(acceptFailed ? { 'aria-describedby': errorId } : {})}
            >
              {accepting ? t('terms.gate.accepting') : t('terms.gate.accept')}
            </Button>
            <Button
              variant="ghost"
              onClick={() => {
                void logout({ logoutParams: { returnTo: window.location.origin } });
              }}
            >
              {t('terms.gate.decline')}
            </Button>
          </div>
        </section>
      </main>
    </div>
  );
}
